import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, RefreshCw, Eye, Trash2, Send, Undo2, Save, PenLine, X, Languages, Loader2 } from "lucide-react";

const LANGS = ["tr", "es", "pt", "ru"];

function relDate(iso) {
    return iso ? new Date(iso).toLocaleString() : "—";
}

function TranslationBadges({ post, onTranslate, onOpen }) {
    const busy = post.translating || [];
    const done = post.translations || {};
    const missing = LANGS.filter((l) => !done[l] && !busy.includes(l));
    return (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid={`admin-blog-translations-${post.id}`}>
            <Languages className="w-3.5 h-3.5 text-muted-foreground" />
            {LANGS.map((l) => {
                const t = done[l];
                const isBusy = busy.includes(l);
                const cls = isBusy
                    ? "border-sky-500/40 text-sky-300 bg-sky-500/10"
                    : t?.status === "published"
                        ? "border-emerald-500/30 text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20"
                        : t
                            ? "border-yellow-500/30 text-yellow-300 bg-yellow-500/10 hover:bg-yellow-500/20"
                            : "border-white/10 text-muted-foreground/60";
                return (
                    <button
                        key={l}
                        type="button"
                        disabled={!t || isBusy}
                        onClick={() => t && onOpen(t.id)}
                        title={isBusy ? "Çevriliyor…" : t ? `${t.status === "published" ? "Yayında" : "Taslak"} · düzenlemek için tıkla` : "Çeviri yok"}
                        data-testid={`admin-blog-lang-${l}-${post.id}`}
                        data-state={isBusy ? "translating" : t ? t.status : "missing"}
                        className={`inline-flex items-center gap-1 h-6 px-2 rounded-full border text-[10px] font-mono uppercase tracking-wider transition-colors ${cls} ${t ? "cursor-pointer" : "cursor-default"}`}
                    >
                        {isBusy && <Loader2 className="w-3 h-3 animate-spin" />}{l}
                    </button>
                );
            })}
            {post.status === "published" && (
                <Button size="sm" variant="outline" onClick={() => onTranslate(post, missing.length ? missing : LANGS, !missing.length)} disabled={busy.length > 0} data-testid={`admin-blog-translate-${post.id}`} className="h-6 px-2 border-white/10 text-[10px]">
                    {busy.length ? "Çevriliyor…" : missing.length ? `Çevir (${missing.length} dil)` : "Yeniden çevir"}
                </Button>
            )}
        </div>
    );
}

function Editor({ post, onClose, onSaved }) {
    const [form, setForm] = useState({ ...post, keywords: (post.keywords || []).join(", ") });
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState("edit");
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                title: form.title, slug: form.slug, description: form.description, category: form.category,
                keywords: form.keywords.split(",").map((k) => k.trim()).filter(Boolean),
                content_html: form.content_html, cover: form.cover, author: form.author,
            };
            const { data } = await api.patch(`/admin/blog/posts/${post.id}`, payload);
            toast.success("Kaydedildi");
            onSaved(data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Kaydedilemedi");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="blog-editor" className="bg-card border border-primary/30 rounded-2xl p-4 md:p-6 space-y-4">
            <div className="flex items-center justify-between gap-3">
                <h3 className="font-display font-semibold text-lg inline-flex items-center gap-2"><PenLine className="w-4 h-4 text-primary" /> Düzenle{post.language && post.language !== "en" && <span data-testid="blog-editor-lang" className="text-[10px] font-mono uppercase px-2 py-0.5 rounded-full border border-sky-500/30 text-sky-300 bg-sky-500/10">{post.language.toUpperCase()} çevirisi</span>}</h3>
                <div className="flex gap-1 bg-background/60 border border-white/10 rounded-lg p-0.5">
                    {[["edit", "Düzenle"], ["preview", "Önizle"]].map(([v, l]) => (
                        <button key={v} onClick={() => setTab(v)} data-testid={`blog-editor-tab-${v}`} className={`px-3 h-8 text-xs rounded-md ${tab === v ? "bg-white/10" : "text-muted-foreground"}`}>{l}</button>
                    ))}
                </div>
                <button onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Close" data-testid="blog-editor-close"><X className="w-4 h-4" /></button>
            </div>
            {tab === "edit" ? (
                <div className="grid md:grid-cols-2 gap-3">
                    <label className="space-y-1 md:col-span-2"><span className="text-[10px] font-mono uppercase text-muted-foreground">Başlık</span><Input data-testid="blog-editor-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="bg-background/60 border-white/10" /></label>
                    <label className="space-y-1"><span className="text-[10px] font-mono uppercase text-muted-foreground">Slug (URL)</span><Input data-testid="blog-editor-slug" value={form.slug} disabled={post.language && post.language !== "en"} onChange={(e) => set("slug", e.target.value)} className="bg-background/60 border-white/10 font-mono text-xs" /></label>
                    <label className="space-y-1"><span className="text-[10px] font-mono uppercase text-muted-foreground">Kategori</span><Input data-testid="blog-editor-category" value={form.category} onChange={(e) => set("category", e.target.value)} className="bg-background/60 border-white/10" /></label>
                    <label className="space-y-1 md:col-span-2"><span className="text-[10px] font-mono uppercase text-muted-foreground">Meta açıklama ({form.description.length}/160)</span><Textarea data-testid="blog-editor-description" rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} className="bg-background/60 border-white/10" /></label>
                    <label className="space-y-1 md:col-span-2"><span className="text-[10px] font-mono uppercase text-muted-foreground">Anahtar kelimeler (virgülle)</span><Input data-testid="blog-editor-keywords" value={form.keywords} onChange={(e) => set("keywords", e.target.value)} className="bg-background/60 border-white/10" /></label>
                    <label className="space-y-1 md:col-span-2"><span className="text-[10px] font-mono uppercase text-muted-foreground">İçerik (HTML)</span><Textarea data-testid="blog-editor-content" rows={18} value={form.content_html} onChange={(e) => set("content_html", e.target.value)} className="bg-background/60 border-white/10 font-mono text-xs" /></label>
                </div>
            ) : (
                <article data-testid="blog-editor-preview" className="prose prose-invert max-w-none prose-headings:font-display prose-a:text-primary" dangerouslySetInnerHTML={{ __html: form.content_html }} />
            )}
            <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={onClose} className="border-white/10">Vazgeç</Button>
                <Button onClick={save} disabled={saving} data-testid="blog-editor-save" className="bg-primary hover:bg-primary/90 text-primary-foreground"><Save className="w-4 h-4 mr-2" /> Kaydet</Button>
            </div>
        </div>
    );
}

export default function AdminBlog() {
    const [posts, setPosts] = useState([]);
    const [auto, setAuto] = useState(null);
    const [filter, setFilter] = useState("");
    const [editing, setEditing] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [topic, setTopic] = useState("");

    const load = useCallback(async () => {
        const [p, a] = await Promise.allSettled([api.get("/admin/blog/posts", { params: filter ? { status: filter } : {} }), api.get("/admin/blog/autopilot")]);
        if (p.status === "fulfilled") setPosts(p.value.data);
        if (a.status === "fulfilled") setAuto(a.value.data);
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    const anyTranslating = posts.some((p) => (p.translating || []).length > 0);
    useEffect(() => {
        if (!anyTranslating) return;
        const t = setInterval(load, 6000);
        return () => clearInterval(t);
    }, [anyTranslating, load]);

    const translate = async (p, langs, force) => {
        try {
            const { data } = await api.post(`/admin/blog/posts/${p.id}/translate`, null, { params: { langs: langs.join(","), force } });
            toast.message(data.started?.length ? `Çeviri başladı: ${data.started.map((l) => l.toUpperCase()).join(", ")} (1–3 dk)` : "Çevrilecek dil yok");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Çeviri başlatılamadı");
        }
    };

    const openById = async (id) => {
        const { data } = await api.get(`/admin/blog/posts/${id}`);
        setEditing(data);
    };

    const generate = async () => {
        setGenerating(true);
        toast.message("AI taslak üretiyor… (30–90 sn)");
        try {
            const { data } = await api.post("/admin/blog/generate", null, { params: topic.trim() ? { topic: topic.trim() } : {}, timeout: 180000 });
            toast.success(`Taslak hazır: ${data.title}`);
            setTopic("");
            await load();
            setEditing(data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Üretim başarısız");
        } finally {
            setGenerating(false);
        }
    };

    const publish = async (p, value) => {
        try {
            const { data } = await api.post(`/admin/blog/posts/${p.id}/publish`, null, { params: { publish: value } });
            toast.success(value ? (data.translating?.length ? `Yayınlandı · ${data.translating.map((l) => l.toUpperCase()).join(", ")} çevirisi arka planda başladı` : "Yayınlandı") : "Taslağa alındı");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "İşlem başarısız");
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`"${p.title}" silinsin mi?`)) return;
        await api.delete(`/admin/blog/posts/${p.id}`);
        toast.success("Silindi");
        load();
    };

    const openEditor = async (p) => {
        const { data } = await api.get(`/admin/blog/posts/${p.id}`);
        setEditing(data);
    };

    return (
        <div data-testid="admin-blog" className="space-y-6 md:space-y-8">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <h1 className="font-display font-bold text-2xl md:text-4xl tracking-tight">Blog Otomasyonu</h1>
                    <p className="text-muted-foreground mt-1 text-sm">Her hafta AI bir SEO makalesi taslağı üretir; siz inceleyip yayınlarsınız. Yayınlanan yazılar otomatik olarak TR · ES · PT · RU dillerine yerelleştirilir ve /tr /es /pt /ru altında aynı adresle yayınlanır.</p>
                </div>
                <Button variant="outline" onClick={load} className="h-10 border-white/10" data-testid="admin-blog-refresh"><RefreshCw className="w-4 h-4 mr-2" /> Yenile</Button>
            </div>

            <div data-testid="admin-blog-autopilot" className={`rounded-2xl border px-4 py-3 text-sm flex flex-col md:flex-row md:items-center gap-2 md:gap-6 ${auto?.enabled ? "border-emerald-500/25 bg-emerald-500/[0.05]" : "border-yellow-500/25 bg-yellow-500/[0.05]"}`}>
                <span className="inline-flex items-center gap-2 font-medium"><Sparkles className={`w-4 h-4 ${auto?.enabled ? "text-emerald-400" : "text-yellow-300"}`} /> Otomatik pilot {auto?.enabled ? "aktif · haftada 1 taslak" : "kapalı (LLM anahtarı yok)"}</span>
                <span className="text-muted-foreground font-mono text-xs">Son üretim: {relDate(auto?.last_generated_at)}</span>
                <span className="text-muted-foreground font-mono text-xs">Sıradaki: {auto?.next_due_at === "now" ? "ilk fırsatta" : relDate(auto?.next_due_at)}</span>
                <span className="text-muted-foreground font-mono text-xs">{auto?.drafts ?? 0} taslak · {auto?.published ?? 0} yayında · {auto?.translations_published ?? 0} çeviri{auto?.translating ? ` · ${auto.translating} çevriliyor` : ""}</span>
            </div>

            <div className="bg-card border border-white/[0.07] rounded-2xl p-4 md:p-5 flex flex-col md:flex-row gap-2 md:items-center">
                <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="İsteğe bağlı konu (boş bırakırsanız AI seçer) — örn. TikTok agency account for e-commerce" data-testid="admin-blog-topic" className="bg-background/60 border-white/10 h-11 flex-1" />
                <Button onClick={generate} disabled={generating} data-testid="admin-blog-generate" className="h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
                    <Sparkles className={`w-4 h-4 mr-2 ${generating ? "animate-pulse" : ""}`} /> {generating ? "Üretiliyor…" : "Şimdi taslak üret"}
                </Button>
            </div>

            {editing && <Editor key={editing.id} post={editing} onClose={() => setEditing(null)} onSaved={(d) => { setEditing(d); load(); }} />}

            <div className="flex gap-1 bg-background/60 border border-white/10 rounded-lg p-0.5 w-max">
                {[["", "Tümü"], ["draft", "Taslaklar"], ["published", "Yayında"]].map(([v, l]) => (
                    <button key={v || "all"} onClick={() => setFilter(v)} data-testid={`admin-blog-filter-${v || "all"}`} className={`px-3 h-9 text-xs font-medium rounded-md ${filter === v ? "bg-white/10" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
                ))}
            </div>

            <div className="bg-card border border-white/[0.07] rounded-2xl overflow-hidden">
                {posts.length === 0 ? (
                    <p className="px-5 py-14 text-center text-sm text-muted-foreground" data-testid="admin-blog-empty">Henüz yazı yok. "Şimdi taslak üret" ile başlayın.</p>
                ) : (
                    <div className="divide-y divide-white/[0.05]">
                        {posts.map((p) => (
                            <div key={p.id} data-testid={`admin-blog-row-${p.id}`} className="px-4 md:px-5 py-4 flex flex-col md:flex-row md:items-center gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span data-testid={`admin-blog-status-${p.id}`} className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border ${p.status === "published" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-yellow-500/15 text-yellow-300 border-yellow-500/30"}`}>{p.status === "published" ? "Yayında" : "Taslak"}</span>
                                        <span className="text-[10px] font-mono text-muted-foreground">{p.source === "ai" ? "AI" : "Manuel"} · {p.category} · {p.reading_time}</span>
                                    </div>
                                    <p className="font-medium text-sm mt-1 truncate">{p.title}</p>
                                    <p className="text-[11px] font-mono text-muted-foreground truncate">/blog/{p.slug} · {p.date}</p>
                                    <div className="mt-2"><TranslationBadges post={p} onTranslate={translate} onOpen={openById} /></div>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <Button size="sm" variant="outline" onClick={() => openEditor(p)} data-testid={`admin-blog-edit-${p.id}`} className="h-9 border-white/10 text-xs"><PenLine className="w-3.5 h-3.5 mr-1" /> Düzenle</Button>
                                    {p.status === "published" ? (
                                        <>
                                            <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`admin-blog-view-${p.id}`} className="inline-flex items-center h-9 px-3 rounded-md border border-white/10 text-xs hover:bg-white/[0.05]"><Eye className="w-3.5 h-3.5 mr-1" /> Gör</a>
                                            <Button size="sm" variant="outline" onClick={() => publish(p, false)} data-testid={`admin-blog-unpublish-${p.id}`} className="h-9 border-white/10 text-xs"><Undo2 className="w-3.5 h-3.5 mr-1" /> Taslağa al</Button>
                                        </>
                                    ) : (
                                        <Button size="sm" onClick={() => publish(p, true)} data-testid={`admin-blog-publish-${p.id}`} className="h-9 bg-emerald-600 hover:bg-emerald-500 text-white text-xs"><Send className="w-3.5 h-3.5 mr-1" /> Yayınla</Button>
                                    )}
                                    <Button size="sm" variant="ghost" onClick={() => remove(p)} data-testid={`admin-blog-delete-${p.id}`} className="h-9 text-muted-foreground hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
